/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, VisuallyHidden, breakpoints } from '@coursera/cds-core';

import { CDS_GRID_LARGE_PADDING, CDS_GRID_PADDING } from 'bundles/cds-labs/components/OverflowCarousel/constants';

import _t from 'i18n!nls/cds-labs';

const styles = {
  container: css`
    position: absolute;
    list-style: none;
    padding: 0 ${CDS_GRID_PADDING}px;
    margin: 0;
    left: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  containerMedium: css`
    ${breakpoints.only('md')} {
      padding-left: ${CDS_GRID_LARGE_PADDING}px;
    }
  `,
  item: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  button: css`
    padding: 0;
  `,
  inViewIndicator: css`
    width: 20px;
    height: 8px;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-primary-weak);

    &:focus,
    &:focus-visible,
    &:hover {
      background-color: var(--cds-color-neutral-primary-weak);
    }

    &:hover {
      box-shadow: 0 0 0 1px var(--cds-color-neutral-primary-weak);
    }
  `,
  notInViewIndicator: css`
    width: 8px;
    height: 8px;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-stroke-primary);

    &:focus,
    &:focus-visible,
    &:hover {
      background-color: var(--cds-color-neutral-stroke-primary);
    }

    &:hover {
      box-shadow: 0 0 0 1px var(--cds-color-neutral-stroke-primary);
    }
  `,
  keyboardFocusOnly: css`
    opacity: 0;
    pointer-events: none;
    transition: opacity 450ms ease-in-out;

    &:focus-within {
      opacity: 1;
      pointer-events: auto;
    }
  `,
};

type Props = {
  numberOfItems: number;
  navigateToItem?: (index: number) => () => void;
  itemsInView: string[];
  // when true, the navigation buttons will be indented to align with the CDS grid at the medium breakpoint.
  indentToAlignWithGridAtMediumBreakpoint?: boolean;
  // when true, the navigation buttons are visually hidden by default and only appear on keyboard focus.
  keyboardFocusOnly?: boolean;
};

const NavigationButtons = ({
  numberOfItems,
  navigateToItem,
  itemsInView,
  indentToAlignWithGridAtMediumBreakpoint,
  keyboardFocusOnly,
}: Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tabbableIndex = itemsInView.length > 0 ? Number(itemsInView[0]) : 0;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('button');
    if (!buttons?.length) return;

    const currentIndex = Array.from(buttons).indexOf(event.target as HTMLButtonElement);
    if (currentIndex === -1) return;

    const nextIndex =
      event.key === 'ArrowRight' ? Math.min(currentIndex + 1, buttons.length - 1) : Math.max(currentIndex - 1, 0);

    if (nextIndex !== currentIndex) {
      event.preventDefault();
      buttons[nextIndex]?.focus();
      navigateToItem?.(nextIndex)();
    }
  };

  if (numberOfItems <= 1) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      role="toolbar"
      aria-label={_t('Carousel navigation')}
      onKeyDown={handleKeyDown}
      css={[
        styles.container,
        indentToAlignWithGridAtMediumBreakpoint && styles.containerMedium,
        keyboardFocusOnly && styles.keyboardFocusOnly,
      ]}
    >
      {Array.from(Array(numberOfItems).keys()).map((value) => (
        <div key={value} css={styles.item}>
          <Button
            variant="ghost"
            size="small"
            onClick={navigateToItem?.(value)}
            tabIndex={value === tabbableIndex ? 0 : -1}
            css={[
              styles.button,
              itemsInView.includes(value.toString()) ? styles.inViewIndicator : styles.notInViewIndicator,
            ]}
          >
            <VisuallyHidden>{_t('Go to item #{index}', { index: value + 1 })}</VisuallyHidden>
          </Button>
        </div>
      ))}
    </div>
  );
};

export default NavigationButtons;
